import ArrowPathIcon from '@heroicons/react/24/outline/ArrowPathIcon';
import { useEffect, useId, useMemo, useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { Button } from '@/components/core/Button';
import { Callout } from '@/components/core/Callout';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
import { DropField } from '@/components/core/Uploader/DropField';
import { useTranslation } from '@/hooks/useTranslation';

const IMAGE_ACCEPT = { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/webp': ['.webp'] };

interface DamReplaceImageDialogProps {
    currentImageUrl: string | undefined;
    currentFilename: string;
    /** Images accept PNG, JPG and WEBP; documents any file. */
    isImage: boolean;
    /** Descriptions of the linked articles, listed as affected. */
    affectedArticles: string[];
    /** Upload progress in percent while replacing; `undefined` before confirmation. */
    progress?: number;
    /** Error shown after a failed replace. */
    errorMessage?: string;
    onConfirm: (file: File) => void;
    onClose: () => void;
}

/**
 * "Change image" (Make "Replace Image", D-043): drop field, old and new image side by side,
 * the affected articles, and only the hints that hold today — links and asset data are kept
 * and the public URL changes; no review reset is announced (LAX-10531).
 */
function DamReplaceImageDialog({
    currentImageUrl,
    currentFilename,
    isImage,
    affectedArticles,
    progress,
    errorMessage,
    onConfirm,
    onClose,
}: DamReplaceImageDialogProps) {
    const { t } = useTranslation();
    const helperId = useId();
    const [file, setFile] = useState<File | undefined>(undefined);
    const [isRejected, setIsRejected] = useState(false);
    const isRunning = progress !== undefined;
    const newImageUrl = useMemo(() => (file && file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined), [file]);

    useEffect(() => () => {
        if (newImageUrl) {
            URL.revokeObjectURL(newImageUrl);
        }
    }, [newImageUrl]);

    const preview = (label: string, badge: string, url: string | undefined, name: string) => (
        <figure className="tw-m-0 tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">{label}</span>
                <Badge variant="neutral">{badge}</Badge>
            </div>
            <div className="tw-flex tw-aspect-[4/3] tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-tertiary tw-bg-fill-secondary">
                {url
                    ? <img src={url} alt={name} className="tw-h-full tw-w-full tw-object-contain" />
                    : <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('DAM.CARD.NO_PREVIEW')}</span>}
            </div>
            <figcaption className="tw-truncate tw-text-t-xs tw-text-t-secondary lg:tw-text-t-sm" title={name}>{name}</figcaption>
        </figure>
    );

    return (
        <Dialog open onOpenChange={(open) => !open && !isRunning && onClose()}>
            <DialogContent variant="large">
                <DialogHeader>
                    <DialogHeaderDefault closeLabel={t('CLOSE')}>{t('DAM.DETAIL.REPLACE_TITLE')}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-flex tw-flex-col tw-gap-4">
                    <Callout variant="info" canBeClosed={false}>
                        <span className="tw-text-t-sm lg:tw-text-t-base">
                            {t('DAM.DETAIL.REPLACE_KEEPS', { count: affectedArticles.length.toLocaleString() })}
                        </span>
                    </Callout>
                    {affectedArticles.length > 0 && (
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <span className="tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">
                                {t('DAM.DETAIL.AFFECTED_ARTICLES', { count: affectedArticles.length.toLocaleString() })}
                            </span>
                            <ul className="tw-m-0 tw-flex tw-max-h-40 tw-list-none tw-flex-col tw-gap-1 tw-overflow-y-auto tw-p-0">
                                {affectedArticles.map((article) => (
                                    <li key={article} className="tw-rounded-lg tw-bg-fill-secondary tw-px-3 tw-py-2 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                        {article}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {file ? (
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row">
                                {preview(t('DAM.DETAIL.REPLACE_CURRENT'), t('DAM.DETAIL.REPLACE_OLD'), currentImageUrl, currentFilename)}
                                {preview(t('DAM.DETAIL.REPLACE_NEW_IMAGE'), t('DAM.DETAIL.REPLACE_NEW'), newImageUrl, file.name)}
                            </div>
                            <Button variant="ghost" disabled={isRunning} onClick={() => setFile(undefined)} className="tw-self-start tw-underline">
                                {t('DAM.DETAIL.REPLACE_PICK_OTHER')}
                            </Button>
                        </div>
                    ) : (
                        <DropField
                            accept={isImage ? IMAGE_ACCEPT : undefined}
                            helperText={isImage ? t('DAM.DETAIL.REPLACE_FORMATS') : undefined}
                            helperTextId={helperId}
                            errorMessage={isRejected ? t('DAM.DETAIL.REPLACE_REJECTED') : undefined}
                            errorMessageId={`${helperId}-error`}
                            onFilesAdded={(files) => {
                                setIsRejected(false);
                                setFile(files[0]);
                            }}
                            onFilesRejected={() => setIsRejected(true)}
                        />
                    )}
                    {isRunning && (
                        <Callout variant="info" canBeClosed={false}>
                            <span className="tw-text-t-sm lg:tw-text-t-base" role="status">
                                {t('DAM.DETAIL.REPLACE_PROGRESS', { percent: Math.round(progress).toLocaleString() })}
                            </span>
                        </Callout>
                    )}
                    {errorMessage && !isRunning && (
                        <Callout variant="destructive" canBeClosed={false}>
                            <span className="tw-text-t-sm lg:tw-text-t-base">{errorMessage}</span>
                        </Callout>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button variant="outline" disabled={isRunning} onClick={onClose}>{t('CANCEL')}</Button>
                    <Button
                        hasIcon
                        icon={ArrowPathIcon}
                        loading={isRunning}
                        disabled={!file || isRunning}
                        onClick={() => file && onConfirm(file)}
                    >
                        {t('DAM.DETAIL.REPLACE_CONFIRM')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { DamReplaceImageDialog };
export type { DamReplaceImageDialogProps };
